
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { MessageSquare, Users, BadgeCheck } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const ProfileHeader = ({ profile }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: followersCount = 0 } = useQuery({
    queryKey: ['followers-count', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;
      const { count, error } = await supabase
        .from('user_follows')
        .select('*', { count: 'exact', head: true })
        .eq('following_id', profile.id);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!profile?.id,
  });

  const { data: followingCount = 0 } = useQuery({
    queryKey: ['following-count', profile?.id],
    queryFn: async () => {
      if (!profile?.id) return 0;
      const { count, error } = await supabase
        .from('user_follows')
        .select('*', { count: 'exact', head: true })
        .eq('follower_id', profile.id);

      if (error) throw error;
      return count || 0;
    },
    enabled: !!profile?.id,
  });

  const { data: isFollowing } = useQuery({
    queryKey: ['is-following', profile?.id],
    queryFn: async () => {
      if (!user?.id || !profile?.id) return false;
      const { data, error } = await supabase
        .from('user_follows')
        .select('*')
        .eq('follower_id', user.id)
        .eq('following_id', profile.id)
        .maybeSingle();

      if (error) throw error;
      return !!data;
    },
    enabled: !!user?.id && !!profile?.id,
  });

  const followMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !profile?.id) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('user_follows')
        .insert([
          {
            follower_id: user.id,
            following_id: profile.id,
          },
        ]);

      if (error) throw error;

      // Send notification using edge function with correct URL format
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/notify-follow`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          followerId: user.id,
          followingId: profile.id,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to send notification');
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followers-count'] });
      queryClient.invalidateQueries({ queryKey: ['is-following'] });
      toast({
        title: "Success",
        description: `You are now following ${profile.username}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const unfollowMutation = useMutation({
    mutationFn: async () => {
      if (!user?.id || !profile?.id) throw new Error('Not authenticated');
      
      const { error } = await supabase
        .from('user_follows')
        .delete()
        .eq('follower_id', user.id)
        .eq('following_id', profile.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['followers-count'] });
      queryClient.invalidateQueries({ queryKey: ['is-following'] });
      toast({
        title: "Success",
        description: `You have unfollowed ${profile.username}`,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const handleFollowClick = () => {
    if (isFollowing) {
      unfollowMutation.mutate();
    } else {
      followMutation.mutate();
    }
  };

  return (
    <>
      <div className="relative">
        <div className="h-48 rounded-xl bg-gradient-to-r from-flux-purple/20 to-flux-blue/20">
          {profile.banner_url && (
            <img 
              src={profile.banner_url} 
              alt="Profile banner"
              className="h-full w-full object-cover rounded-xl"
            />
          )}
        </div>
        <div className="absolute -bottom-16 left-8">
          <Avatar className="h-32 w-32 border-4 border-flux-black">
            <AvatarImage src={profile.avatar_url || "/placeholder.svg"} />
            <AvatarFallback>{profile.full_name?.charAt(0) || "U"}</AvatarFallback>
          </Avatar>
        </div>
      </div>

      <div className="pt-20 flex flex-col md:flex-row md:justify-between md:items-start">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold">{profile.full_name}</h1>
            {profile.is_pro && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <BadgeCheck className="h-6 w-6 text-flux-cyan" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Pro Member</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <div className="flex items-center gap-2">
            <p className="text-gray-400">@{profile.username}</p>
            {!profile.is_pro && (
              <Button
                variant="ghost"
                size="sm"
                className="text-flux-cyan hover:text-flux-cyan/80"
                onClick={() => {
                  toast({
                    title: "Coming Soon!",
                    description: "You'll be able to subscribe to Pro soon which unlocks a range of features like crowdfunding, Tokens for Vesu AI, suggested recruits, pro badge and much more!",
                  });
                }}
              >
                <BadgeCheck className="h-4 w-4 mr-1" />
                Get Pro
              </Button>
            )}
          </div>
          {profile.bio && (
            <p className="text-flux-cyan mt-2">{profile.bio}</p>
          )}
          <div className="flex gap-4 mt-4 text-gray-400">
            <span className="flex items-center gap-2">
              <Users size={16} />
              {followersCount} followers
            </span>
            <span>•</span>
            <span>{followingCount} following</span>
          </div>
        </div>
        <div className="flex gap-3 mt-4 md:mt-0">
          {user?.id !== profile.id && (
            <Button 
              variant="outline" 
              className={`${
                isFollowing 
                  ? 'border-red-500 text-red-500 hover:bg-red-500/10' 
                  : 'border-flux-purple text-flux-purple hover:bg-flux-black'
              } bg-flux-black`}
              onClick={handleFollowClick}
              disabled={followMutation.isPending || unfollowMutation.isPending}
            >
              {isFollowing ? 'Unfollow' : 'Follow'}
            </Button>
          )}
          <Button variant="outline" className="border-flux-purple text-flux-purple hover:bg-flux-black bg-flux-black">
            <MessageSquare className="mr-2 h-4 w-4" />
            Message
          </Button>
        </div>
      </div>
    </>
  );
};
